import { Provider, useDispatch, useSelector } from 'react-redux';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useEffect, useRef } from 'react';
import { loadSchema, reset } from './store/slice';
import { selectIsDirty } from './store/selectors';
import { store } from './store/store';
import { Calculation, Worksheet } from '../../../types/worksheet';
import EmptyStateNew from '../../../components/empty-state/EmptyStateNew';
import SchemaForm from './SchemaForm';
import ErrorBoundary from '../../../ErrorBoundary';
import SchemaPersistance from './SchemaPersistance';

type SchemaContainerProps = {
    worksheet?: Worksheet;
    calculation: Calculation;
};

function SchemaContainer({ worksheet, calculation }: SchemaContainerProps): JSX.Element {
    return (
        <div className="h-full [&>div]:mt-0">
            <ErrorBoundary>
                <Provider store={store}>
                    <LoadSchemaWrapper worksheet={worksheet} calculation={calculation} />
                </Provider>
            </ErrorBoundary>
        </div>
    );
}

function LoadSchemaWrapper({ worksheet, calculation }: SchemaContainerProps): JSX.Element {
    const dispatch = useDispatch();
    const isDirty = useSelector(selectIsDirty);

    useEffect(() => {
        if (!isDirty) {
            dispatch(
                loadSchema({
                    calculation: calculation,
                    schema: calculation.schema,
                    worksheet,
                })
            );
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, calculation]);

    useEffect(() => {
        return () => {
            dispatch(reset());
        };
    }, [calculation.id, dispatch]);

    return (
        <div className="flex h-full">
            {calculation ? (
                <>
                    <SchemaPersistance worksheetId={worksheet?.id ?? ''} calculation={calculation} />
                    <SchemaForm worksheetId={worksheet?.id ?? ''} calculation={calculation} name={worksheet?.name ?? ''} />
                </>
            ) : (
                <div className="self-center mx-auto mt-32">
                    <EmptyStateNew
                        title="No schema found"
                        helpText="We were unable to load the schema for this custom worksheet. Please try refreshing the page."
                        icon={<ExclamationTriangleIcon />}
                    />
                </div>
            )}
        </div>
    );
}

export default SchemaContainer;
